import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { MapProvider } from '@/components/MapProvider';
import { DeliveryMap } from '@/components/DeliveryMap';
import { useRealTimeTracking } from '@/hooks/useRealTimeTracking';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MapPin, Clock, Package, CheckCircle, Truck, Navigation, User, Phone, ArrowLeft, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

// Public tracking page - no authentication required
const PublicOrderTracking = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentLocation, routeHistory, connectionStatus, isConnected } = useRealTimeTracking(orderId);

  const fetchOrder = useCallback(async () => {
    try {
      // Use public endpoint that doesn't require auth
      const response = await axios.get(`/tracking/public/${orderId}`);
      setOrder(response.data.order);
      setTrackingData(response.data.tracking);
      setError(null);
    } catch (err) {
      console.error('Error fetching order:', err);
      if (err.response?.status === 404) {
        setError('Pedido no encontrado. Verifica el código de seguimiento.');
      } else {
        setError('Error al cargar la información del pedido.');
      }
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    fetchOrder();
    // Refresh every 30 seconds
    const interval = setInterval(fetchOrder, 30000);
    return () => clearInterval(interval);
  }, [fetchOrder]);

  const getStatusSteps = () => [
    { status: 'created', label: 'Creado', icon: Package, color: '#6B7280' },
    { status: 'ready_for_pickup', label: 'Listo para Envío', icon: Clock, color: '#F59E0B' },
    { status: 'in_custody', label: 'En Custodia', icon: Truck, color: '#3B82F6' },
    { status: 'en_route', label: 'En Camino', icon: Navigation, color: '#8B5CF6' },
    { status: 'delivered', label: 'Entregado', icon: CheckCircle, color: '#10B981' }
  ];

  const getCurrentStepIndex = (status) => {
    const statusMap = {
      'CREATED': 0,
      'READY_FOR_PICKUP': 1,
      'IN_CUSTODY': 2,
      'EN_ROUTE': 3,
      'DELIVERED': 4
    };
    return statusMap[status?.toUpperCase()] ?? 0;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#EAE0D5] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#224049] mx-auto mb-4"></div>
          <p className="text-[#224049]">Cargando información del pedido...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#EAE0D5] flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <Package className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h2 className="text-xl font-semibold text-[#224049] mb-2">No se encontró el pedido</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <Button onClick={fetchOrder} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />
              Intentar de nuevo
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!order) return null;

  const currentStepIndex = getCurrentStepIndex(order.status);
  const steps = getStatusSteps();
  const riderLocation = currentLocation || trackingData?.current_location;

  // Prepare delivery stop for map
  const deliveryStop = order.delivery_address ? {
    id: order.id,
    address: order.delivery_address.line1,
    latitude: order.delivery_address.lat || order.delivery_address.latitude || 0,
    longitude: order.delivery_address.lng || order.delivery_address.longitude || 0,
    status: order.status === 'DELIVERED' ? 'completed' : order.status === 'EN_ROUTE' ? 'in_progress' : 'pending',
    customer_name: order.customer?.name
  } : null;

  return (
    <div className="min-h-screen bg-[#EAE0D5]">
      {/* Header */}
      <header className="bg-[#224049] text-white py-4 px-6 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Package className="w-8 h-8 text-[#D4AD6A]" />
            <div>
              <h1 className="text-xl font-bold">UID Delivery</h1>
              <p className="text-sm text-gray-300">Seguimiento de Pedido</p>
            </div>
          </div>
          <Badge className={`${order.mode === 'EXPRESS' ? 'bg-[#C38154]' : 'bg-[#0F7A5B]'}`}>
            {order.mode === 'EXPRESS' ? 'Express 60\'' : 'Estándar'}
          </Badge>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 md:p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel - Order Info */}
          <div className="lg:col-span-1 space-y-4">
            {/* Order Details */}
            <Card className="bg-white">
              <CardHeader className="pb-2">
                <CardTitle className="text-[#224049] text-lg flex items-center gap-2">
                  <Package className="w-5 h-5 text-[#C38154]" />
                  Pedido #{order.id?.substring(0, 8)}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <User className="w-4 h-4 mt-1 text-[#C38154]" />
                  <div>
                    <p className="text-sm font-medium text-[#224049]">{order.customer?.name}</p>
                    <p className="text-xs text-gray-500">{order.customer?.phone}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 mt-1 text-[#C38154]" />
                  <div>
                    <p className="text-sm text-gray-700">{order.delivery_address?.line1}</p>
                    <p className="text-xs text-gray-500">
                      {order.delivery_address?.city}, {order.delivery_address?.postal_code}
                    </p>
                  </div>
                </div>

                {order.eta_minutes && order.status === 'EN_ROUTE' && (
                  <div className="bg-[#F5F1ED] p-3 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-[#C38154]" />
                      <span className="text-sm font-medium text-[#224049]">
                        Llegada estimada: ~{order.eta_minutes} minutos
                      </span>
                    </div>
                  </div>
                )}

                {/* Route info */}
                {order.route_distance_meters && (
                  <div className="pt-2 border-t border-gray-100">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Distancia:</span>
                      <span className="font-medium">{(order.route_distance_meters / 1000).toFixed(1)} km</span>
                    </div>
                    <div className="flex justify-between text-sm mt-1">
                      <span className="text-gray-600">Tiempo estimado:</span>
                      <span className="font-medium">{Math.round(order.route_duration_seconds / 60)} min</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Status Timeline */}
            <Card className="bg-white">
              <CardHeader className="pb-2">
                <CardTitle className="text-[#224049] text-lg">Estado del Envío</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {steps.map((step, index) => {
                    const StepIcon = step.icon;
                    const isActive = index <= currentStepIndex;
                    const isCurrent = index === currentStepIndex;

                    return (
                      <div key={step.status} className="flex items-center gap-3">
                        <div
                          className={`flex items-center justify-center w-10 h-10 rounded-full transition-all ${
                            isActive ? 'bg-[#224049]' : 'bg-gray-100'
                          } ${isCurrent ? 'ring-2 ring-[#D4AD6A] ring-offset-2' : ''}`}
                        >
                          <StepIcon className={`w-5 h-5 ${isActive ? 'text-white' : 'text-gray-400'}`} />
                        </div>
                        <div className="flex-1">
                          <p className={`text-sm font-medium ${isActive ? 'text-[#224049]' : 'text-gray-400'}`}>
                            {step.label}
                          </p>
                          {isCurrent && (
                            <p className="text-xs text-[#C38154] font-medium animate-pulse">
                              Estado actual
                            </p>
                          )}
                        </div>
                        {index < steps.length - 1 && (
                          <div className={`w-0.5 h-4 ${isActive ? 'bg-[#224049]' : 'bg-gray-200'}`} />
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* WebSocket connection indicator */}
                {trackingData?.tracking_active && (
                  <div className="mt-4 p-3 bg-[#F5F1ED] rounded-lg">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${
                        isConnected ? 'bg-green-500 animate-pulse' : 'bg-gray-400'
                      }`} />
                      <p className="text-xs text-gray-600">
                        {isConnected ? 'Conectado en tiempo real' : 'Reconectando...'}
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Contact Support */}
            <Card className="bg-white">
              <CardContent className="pt-4">
                <p className="text-sm text-gray-600 mb-3">¿Necesitas ayuda con tu pedido?</p>
                <Button variant="outline" className="w-full" asChild>
                  <a href="tel:+528001234567">
                    <Phone className="w-4 h-4 mr-2" />
                    Contactar Soporte
                  </a>
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Right Panel - Map */}
          <Card className="lg:col-span-2 bg-white overflow-hidden">
            <CardHeader className="pb-2">
              <CardTitle className="text-[#224049] text-lg flex items-center gap-2">
                <Navigation className="w-5 h-5 text-[#C38154]" />
                {trackingData?.tracking_active ? 'Ubicación en Tiempo Real' : 'Ubicación de Entrega'}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 h-[500px] md:h-[600px]">
              <MapProvider>
                <DeliveryMap
                  stops={deliveryStop ? [deliveryStop] : []}
                  currentLocation={riderLocation}
                  encodedPolyline={order.route_polyline}
                  showRouteHistory={true}
                  routeHistory={routeHistory}
                  centerOnRider={trackingData?.tracking_active}
                  onMarkerClick={() => {}}
                />
              </MapProvider>
            </CardContent>
          </Card>
        </div>

        {/* Delivered message */}
        {order.status === 'DELIVERED' && (
          <div className="mt-6 text-center">
            <Card className="bg-green-50 border-green-200">
              <CardContent className="py-8">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-green-700 mb-2">¡Pedido Entregado!</h2>
                <p className="text-green-600">Gracias por usar UID Delivery</p>
              </CardContent>
            </Card>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-[#224049] text-white py-4 mt-8">
        <div className="max-w-7xl mx-auto px-6 text-center text-sm text-gray-300">
          <p>© 2024 UID Delivery. Todos los derechos reservados.</p>
        </div>
      </footer>
    </div>
  );
};

export default PublicOrderTracking;
